import { useState, useEffect, useMemo } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useProductDetails, Product, ProductVariant, ProductSku } from '@/hooks/useProducts';
import { useProducts } from '@/hooks/useProducts';
import { useCart } from '@/hooks/useCart';

function RelatedProductCard({ product }: { product: Product }) {
  const { addItem } = useCart();
  const navigate = useNavigate();
  const img = product.media?.[0]?.url ?? '';

  const handleAddToCart = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (product.hasVariants) { navigate(`/product/${product.id}`); return; }
    addItem({
      productId: product.id,
      skuId: null,
      skuLabel: null,
      name: product.name,
      price: product.price,
      image: img,
    });
  };

  const handleBuyNow = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (product.hasVariants) { navigate(`/product/${product.id}`); return; }
    addItem({
      productId: product.id,
      skuId: null,
      skuLabel: null,
      name: product.name,
      price: product.price,
      image: img,
    });
    navigate('/checkout');
  };

  return (
    <div className="group bg-white rounded-lg overflow-hidden border border-[#E8E0D4] hover:shadow-md transition-shadow flex flex-col">
      <Link to={`/product/${product.id}`} className="block relative aspect-square overflow-hidden bg-white p-3 md:p-4">
        {img && (
          <img src={img} alt={product.name} className="w-full h-full object-contain" loading="lazy" />
        )}
      </Link>
      <div className="p-3 md:p-4 flex flex-col flex-1">
        <Link to={`/product/${product.id}`}>
          <h3 className="font-serif text-sm md:text-base font-semibold text-[#1A1A1A] group-hover:text-[#4f528d] transition-colors mb-1 leading-snug text-center">{product.name}</h3>
        </Link>
        <p className="text-base md:text-lg font-semibold text-[#2c2a4a] text-center mb-3">${product.price.toFixed(2)}</p>
        <div className="mt-auto grid grid-cols-2 gap-2">
          <button
            onClick={handleAddToCart}
            className="py-2 px-2 rounded-md border border-[#4f528d] text-[#4f528d] text-[11px] font-semibold uppercase tracking-tight hover:bg-[#4f528d] hover:text-white transition-colors whitespace-nowrap text-center"
          >
            Add to Cart
          </button>
          <button
            onClick={handleBuyNow}
            className="py-2 px-2 rounded-md bg-[#4f528d] text-white text-[11px] font-semibold uppercase tracking-tight hover:bg-[#3d4070] transition-colors whitespace-nowrap text-center"
          >
            Buy Now
          </button>
        </div>
      </div>
    </div>
  );
}

function findMatchingSku(
  skus: ProductSku[],
  selectedOptions: Record<string, string>
): ProductSku | null {
  return (
    skus.find((sku) => {
      const opts = sku.options ?? [];
      return Object.entries(selectedOptions).every(([_, val]) => opts.includes(val));
    }) ?? null
  );
}

function findVariantImageIndex(
  media: Product['media'],
  selectedOptions: Record<string, string>
): number {
  if (!media || media.length === 0) return 0;
  if (!selectedOptions || Object.keys(selectedOptions).length === 0) return 0;

  for (const optionValue of Object.values(selectedOptions)) {
    const optionLower = optionValue.toLowerCase();
    // Try exact or partial match
    const idx = media.findIndex((img) => {
      if (!img.alt) return false;
      const altLower = img.alt.toLowerCase();
      // Check if alt contains option, or option contains alt, or they share key words
      if (altLower.includes(optionLower) || optionLower.includes(altLower)) return true;
      // Check for shared significant words (longer than 3 chars)
      const altWords = altLower.split(/\s+/).filter((w) => w.length > 3);
      const optionWords = optionLower.split(/\s+/).filter((w) => w.length > 3);
      return altWords.some((aw) => optionWords.includes(aw));
    });
    if (idx !== -1) return idx;
  }

  return 0;
}

export default function ProductDetail() {
  const { id } = useParams<{ id: string }>();
  const { detail, loading, error } = useProductDetails(id);
  const product = detail.product;
  const variants = detail.variants;
  const skus = detail.skus;

  useEffect(() => {
    document.title = product ? `${product.name} | SpinWorld.Us` : 'Product | SpinWorld.Us';
  }, [product]);

  const { addItem } = useCart();
  const navigate = useNavigate();
  const [quantity, setQuantity] = useState(1);
  const [added, setAdded] = useState(false);
  const [selectedOptions, setSelectedOptions] = useState<Record<string, string>>();
  const [activeImageIndex, setActiveImageIndex] = useState(0);

  // Reset selections when product changes
  useEffect(() => {
    setSelectedOptions({});
    setQuantity(1);
    setAdded(false);
    setActiveImageIndex(0);
  }, [id]);

  // Auto-switch image when variants change
  useEffect(() => {
    if (product && product.media && product.media.length > 1) {
      const newIndex = findVariantImageIndex(product.media, selectedOptions);
      setActiveImageIndex(newIndex);
    }
  }, [selectedOptions, product]);

  // Compute selected SKU and effective price
  const selectedSku = useMemo(() => {
    if (skus.length === 0 || variants.length === 0) return null;
    return findMatchingSku(skus, selectedOptions);
  }, [skus, selectedOptions, variants.length]);

  const effectivePrice = useMemo(() => {
    if (selectedSku) {
      return selectedSku.discount_enabled && selectedSku.discount_price != null
        ? selectedSku.discount_price
        : selectedSku.price;
    }
    if (product) {
      return product.discount_enabled && product.discount_price != null
        ? product.discount_price
        : product.price;
    }
    return 0;
  }, [selectedSku, product]);

  // Fetch related products from same category
  const { products: relatedProducts } = useProducts();
  const related = relatedProducts
    .filter((p) => p.id !== product?.id && p.category_id === product?.category_id)
    .slice(0, 4);

  const allOptionsSelected =
    variants.length === 0 ||
    variants.every((v) => selectedOptions[v.name] && selectedOptions[v.name] !== '');

  const handleOptionSelect = (variantName: string, option: string) => {
    setSelectedOptions((prev) => ({ ...prev, [variantName]: option }));
  };

  const handleAddToCart = () => {
    if (!product) return;
    if (variants.length > 0 && !allOptionsSelected) return;
    const image = product.media?.[activeImageIndex]?.url ?? product.media?.[0]?.url ?? '';
    addItem(
      {
        productId: selectedSku?.id ?? product.id,
        skuId: selectedSku?.id ?? null,
        skuLabel: selectedSku?.label ?? null,
        name: product.name,
        price: effectivePrice,
        image,
      },
      quantity
    );
    setAdded(true);
    setTimeout(() => setAdded(false), 2000);
  };

  const handleBuyNow = () => {
    if (!product) return;
    if (variants.length > 0 && !allOptionsSelected) return;
    const image = product.media?.[activeImageIndex]?.url ?? product.media?.[0]?.url ?? '';
    addItem(
      {
        productId: selectedSku?.id ?? product.id,
        skuId: selectedSku?.id ?? null,
        skuLabel: selectedSku?.label ?? null,
        name: product.name,
        price: effectivePrice,
        image,
      },
      quantity
    );
    navigate('/checkout');
  };

  const mainImage = product?.media?.[activeImageIndex]?.url ?? product?.media?.[0]?.url ?? '';
  const hasMultipleImages = (product?.media?.length ?? 0) > 1;

  if (loading) {
    return (
      <main className="min-h-screen bg-[#f0f4f7] py-16">
        <div className="container-main text-center">
          <div className="w-10 h-10 border-3 border-[#B87333] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-[#8B7355]">Loading product...</p>
        </div>
      </main>
    );
  }

  if (error || !product) {
    return (
      <main className="min-h-screen bg-[#f0f4f7] py-16">
        <div className="container-main text-center">
          <i className="ri-error-warning-line text-4xl text-[#D4C9B8] mb-3" />
          <p className="text-lg text-[#8B7355]">Product not found.</p>
          <Link to="/shop" className="inline-block mt-4 text-[#4f528d] hover:underline">
            Back to Shop
          </Link>
        </div>
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-[#f0f4f7]">
      {/* Breadcrumb */}
      <div className="bg-[#efe4f7] border-b border-[#E8E0D4]">
        <div className="container-main py-4">
          <nav className="text-sm text-[#8B7355]">
            <Link to="/" className="hover:text-[#4f528d] transition-colors">Home</Link>
            <span className="mx-2">/</span>
            <Link to="/shop" className="hover:text-[#4f528d] transition-colors">Shop</Link>
            <span className="mx-2">/</span>
            {product.product_categories?.name && (
              <>
                <Link
                  to={`/collections/${product.product_categories.name.toLowerCase().replace(/\s+/g, '-')}`}
                  className="hover:text-[#4f528d] transition-colors"
                >
                  {product.product_categories.name}
                </Link>
                <span className="mx-2">/</span>
              </>
            )}
            <span className="text-[#1A1A1A]">{product.name}</span>
          </nav>
        </div>
      </div>

      {/* Product Main */}
      <section className="py-10 md:py-16">
        <div className="container-main">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12">
            {/* Images */}
            <div className="space-y-4">
              <div className="aspect-square rounded-lg overflow-hidden bg-white border border-[#E8E0D4]">
                {mainImage && (
                  <img
                    src={mainImage}
                    alt={product.media?.[activeImageIndex]?.alt ?? product.name}
                    className="w-full h-full object-contain"
                  />
                )}
              </div>
              {/* Thumbnail gallery */}
              {hasMultipleImages && (
                <div className="flex gap-2 flex-wrap">
                  {product.media.map((img, idx) => (
                    <button
                      key={idx}
                      onClick={() => setActiveImageIndex(idx)}
                      className={`w-16 h-16 md:w-20 md:h-20 rounded-md overflow-hidden border-2 transition-all bg-white ${
                        idx === activeImageIndex
                          ? 'border-[#4f528d] ring-2 ring-[#4f528d]/20'
                          : 'border-[#D4C9B8] hover:border-[#4f528d]'
                      }`}
                    >
                      <img
                        src={img.url}
                        alt={img.alt ?? product.name}
                        className="w-full h-full object-contain"
                      />
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Info */}
            <div className="flex flex-col">
              {product.product_categories?.name && (
                <p className="text-xs uppercase tracking-wider text-[#4f528d] font-semibold mb-2">
                  {product.product_categories.name}
                </p>
              )}
              <h1 className="font-serif text-2xl md:text-3xl lg:text-4xl font-bold text-[#1A1A1A] mb-3 leading-tight">
                {product.name}
              </h1>

              {/* Price */}
              <p className="text-2xl md:text-3xl font-bold text-[#2c2a4a] mb-6">
                ${effectivePrice.toFixed(2)}
                {selectedSku && selectedSku.label && (
                  <span className="text-sm font-normal text-[#8B7355] ml-2">({selectedSku.label})</span>
                )}
              </p>

              {product.description && (
                <div className="prose prose-sm text-[#4A4A4A] leading-relaxed mb-8">
                  <p>{product.description}</p>
                </div>
              )}

              {/* Variants */}
              {variants.length > 0 && (
                <div className="space-y-5 mb-8">
                  {variants.map((variant) => (
                    <div key={variant.id}>
                      <p className="text-sm font-semibold text-[#1A1A1A] mb-2">
                        {variant.name}
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {variant.options.map((option) => {
                          const isSelected = selectedOptions[variant.name] === option;
                          return (
                            <button
                              key={option}
                              onClick={() => handleOptionSelect(variant.name, option)}
                              className={`px-4 py-2 rounded-md text-sm font-medium border transition-all ${
                                isSelected
                                  ? 'bg-[#4f528d] text-white border-[#4f528d]'
                                  : 'bg-white text-[#1A1A1A] border-[#D4C9B8] hover:border-[#4f528d]'
                              }`}
                            >
                              {option}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                  {!allOptionsSelected && (
                    <p className="text-sm text-amber-600">Please select all options above.</p>
                  )}
                </div>
              )}

              {/* Quantity */}
              <div className="flex items-center gap-4 mb-6">
                <span className="text-sm font-semibold text-[#1A1A1A]">Quantity:</span>
                <div className="flex items-center border border-[#D4C9B8] rounded-md overflow-hidden">
                  <button
                    onClick={() => setQuantity((q) => Math.max(1, q - 1))}
                    className="px-3 py-2 hover:bg-[#d1bedd] transition-colors text-[#1A1A1A]"
                  >
                    <i className="ri-subtract-line" />
                  </button>
                  <span className="px-4 py-2 text-sm font-semibold text-[#1A1A1A] min-w-[3rem] text-center">
                    {quantity}
                  </span>
                  <button
                    onClick={() => setQuantity((q) => q + 1)}
                    className="px-3 py-2 hover:bg-[#d1bedd] transition-colors text-[#1A1A1A]"
                  >
                    <i className="ri-add-line" />
                  </button>
                </div>
              </div>

              {/* Add to Cart / Buy Now / View Cart */}
              <div className="flex flex-col sm:flex-row gap-3 mb-8">
                <button
                  onClick={handleAddToCart}
                  disabled={variants.length > 0 && !allOptionsSelected}
                  className={`flex-1 py-3.5 px-6 rounded-md font-sans font-semibold text-sm uppercase tracking-wider transition-all whitespace-nowrap ${
                    added
                      ? 'bg-[#2c2a4a] text-white'
                      : variants.length > 0 && !allOptionsSelected
                        ? 'bg-gray-300 text-white cursor-not-allowed'
                        : 'bg-[#4f528d] text-white hover:bg-[#3d4070]'
                  }`}
                >
                  {added ? (
                    <span className="flex items-center justify-center gap-2">
                      <i className="ri-check-line" /> Added to Cart
                    </span>
                  ) : (
                    <span className="flex items-center justify-center gap-2">
                      <i className="ri-shopping-bag-line" /> Add to Cart
                    </span>
                  )}
                </button>
                <button
                  onClick={handleBuyNow}
                  disabled={variants.length > 0 && !allOptionsSelected}
                  className={`flex-1 py-3.5 px-6 rounded-md bg-[#4f528d] text-white font-sans font-semibold text-sm uppercase tracking-wider transition-all whitespace-nowrap ${
                    variants.length > 0 && !allOptionsSelected
                      ? 'bg-gray-300 cursor-not-allowed'
                      : 'hover:bg-[#3d4070]'
                  }`}
                >
                  <span className="flex items-center justify-center gap-2">
                    <i className="ri-flashlight-line" /> Buy Now
                  </span>
                </button>
                <Link
                  to="/cart"
                  className="py-3.5 px-6 rounded-md border-2 border-[#4f528d] text-[#4f528d] font-sans font-semibold text-sm uppercase tracking-wider hover:bg-[#4f528d] hover:text-white transition-all text-center whitespace-nowrap"
                >
                  View Cart
                </Link>
              </div>

              {/* Trust badges */}
              <div className="grid grid-cols-2 gap-3 pt-6 border-t border-[#E8E0D4]">
                <div className="flex items-center gap-2 text-sm text-[#8B7355]">
                  <i className="ri-truck-line text-[#2c2a4a]" />
                  <span>Ships from the US</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-[#8B7355]">
                  <i className="ri-shield-check-line text-[#2c2a4a]" />
                  <span>Rustproof Steel</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-[#8B7355]">
                  <i className="ri-arrow-go-back-line text-[#2c2a4a]" />
                  <span>30-Day Returns</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-[#8B7355]">
                  <i className="ri-customer-service-2-line text-[#2c2a4a]" />
                  <span>Customer Support</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Related Products */}
      {related.length > 0 && (
        <section className="py-10 md:py-14 bg-[#efe4f7] border-t border-[#E8E0D4]">
          <div className="container-main">
            <h2 className="font-serif text-2xl md:text-3xl font-bold text-[#1A1A1A] mb-8">You May Also Like</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
              {related.map((p) => (
                <RelatedProductCard key={p.id} product={p} />
              ))}
            </div>
          </div>
        </section>
      )}
    </main>
  );
}